import { useEffect } from "react";
import FormWizardFormWrapper from "../../../Forms/FormWizard/FormWizardFormWrapper";
import FilterProvider from "../../../AppView/FilterManagement/FilterProvider/FilterProvider";
import { filterEntityEnum } from "../../../AppView/FilterManagement/filterEntity";
import ScopeProvider from "../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import FilterAndScopeInputs from "../FilterAndScopeInputs/FilterAndScopeInputs";
import PageFormWidget from "../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import PageFormButtons from "../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { AutomationWizardStepProps } from "../../AutomationsUtils";
import { LS_AUTOMATION_FILTER } from "../../../Findings/FindingsFilters/FilterUtils";
import {
  FieldViewBlock,
  useIcon,
  TextInput,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import { fileTypeIcons } from "../../../Findings/FindingDetailsPane/FindingsDetailsPaneComponents/Attachments/AttachmentsUtils";
import ReportFields from "../../../AppView/DownloadReport/ReportFields/ReportFields";
import { ticketEndpointTypeEnum } from "@SeemplicityDev/ticket-master";
import "./FindingsStep.scss";

const FindingsStep: React.FC<
  AutomationWizardStepProps & {
    automationId?: string;
  }
> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
  automationId,
}) => {
  const methods = useForm({
    mode: "all",
    values: defaultValues,
  });
  const { handleSubmit, control, getValues, setValue, formState } = methods;
  const { getSvg } = useIcon();

  const filter = useWatch({ control, name: "filter" });
  const scope = useWatch({ control, name: "scope" });

  // This effect handles the reset occurring from ReportFields component
  // If columns is in defaultValues (hence, the form is being edited), set the saved form value
  // Otherwise, reapply the value set by ReportFields using setValue. More here: https://betterviewcloud.atlassian.net/browse/WAK-25978.
  useEffect(() => {
    if (
      !!defaultValues &&
      !!formState.defaultValues &&
      !!formState.defaultValues.columns
    ) {
      setValue(
        "columns",
        defaultValues.columns || formState.defaultValues.columns
      );
    }
  }, [defaultValues, formState.defaultValues, setValue]);

  return (
    <FormProvider {...methods}>
      <FormWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <div
            className={`findings-step-container ${isSummary ? "is-summary" : ""}`}
            data-testid={"findings-step-container"}
          >
            <FilterProvider
              lsFilterKey={LS_AUTOMATION_FILTER}
              filtersEntity={filterEntityEnum.REMEDIATION_ITEM}
            >
              <ScopeProvider>
                <FilterAndScopeInputs isSummary={isSummary} />
              </ScopeProvider>
            </FilterProvider>
            <Controller
              name="name"
              control={control}
              rules={{ required: REQUIRED_ERROR_MESSAGE }}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => {
                return (
                  <TextInput
                    label="Name"
                    viewMode={isSummary}
                    labelTop
                    required
                    data-testid="name-input"
                    name="name"
                    placeholder="Report name"
                    error={error}
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                );
              }}
            />
            {!isSummary && (
              <ReportFields
                rmqId={automationId}
                endpointType={ticketEndpointTypeEnum.REPORT}
                description={
                  "Select the data fields to include in your exported Findings report."
                }
                label={"Finding Data"}
                useLocalStorage={false}
                fetchPolicy={
                  defaultValues?.columns ? "cache-first" : "no-cache"
                }
              />
            )}
            {isSummary && (
              <FieldViewBlock
                label={"Format"}
                className={"findings-step-format"}
              >
                <div className="findings-step-format-content">
                  <img
                    src={getSvg(fileTypeIcons.csv)}
                    alt={fileTypeIcons.csv}
                  />
                  <span>CSV</span>
                </div>
              </FieldViewBlock>
            )}
          </div>
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons
            data-testid={"findings-report-form"}
            nextButtonDisabled={!filter || !scope}
          />
        )}
      </FormWizardFormWrapper>
    </FormProvider>
  );
};

export default FindingsStep;
